import { useFilePreview } from '@/contexts/file-preview'
import { SaleTrackingId } from '@/modules/Customer/types'
import { memo, useCallback, useMemo } from 'react'
import GenericFilePreview from './shared/GenericFilePreview'

interface FileItemProps {
    name: string
    size: number
    preview?: string
    thumbnail?: string
    type?: string
    note?: string
    employee?: SaleTrackingId
    onDelete?: () => void
}

const FileItem: React.FC<FileItemProps> = memo((props) => {
    const fileContext = useFilePreview()
    const { name, preview, size, employee, note, type, thumbnail, onDelete } =
        props

    // ✅ Memoize handlePreview callback
    const handlePreview = useCallback(() => {
        if (!preview) return

        fileContext.preview(
            preview,
            undefined,
            {
                attachment_file_name: name,
                attachment_file_size: size,
                attachment_file_note: note,
                attachment_file_type: type,
                employee: employee,
            },
            true
        )
    }, [preview, name, size, note, type, employee, fileContext])

    // ✅ Memoize file extension detection
    const fileExtension = useMemo(():
        | 'Word'
        | 'Excel'
        | 'PowerPoint'
        | 'CSV'
        | 'Video'
        | 'Text'
        | 'Attachment'
        | 'Image'
        | 'PDF' => {
        const fileExtension = name.split('.').pop()?.toLowerCase()
        const mimeType = type?.toLowerCase()

        // Check by MIME type first
        if (mimeType) {
            if (mimeType.startsWith('image/')) {
                return 'Image'
            }
            if (
                mimeType.includes('spreadsheet') ||
                mimeType.includes('excel')
            ) {
                return 'Excel'
            }
            if (
                mimeType.includes('presentation') ||
                mimeType.includes('powerpoint')
            ) {
                return 'PowerPoint'
            }
            if (mimeType.includes('document') || mimeType.includes('word')) {
                return 'Word'
            }
            if (mimeType.startsWith('video/')) {
                return 'Video'
            }
            if (mimeType.startsWith('text/')) {
                return 'Text'
            }
        }

        // Fallback to file extension
        switch (fileExtension) {
            case 'doc':
            case 'docx':
                return 'Word'
            case 'xls':
            case 'xlsx':
                return 'Excel'
            case 'pdf':
                return 'PDF'
            case 'ppt':
            case 'pptx':
                return 'PowerPoint'
            case 'csv':
                return 'CSV'
            case 'txt':
            case 'rtf':
                return 'Text'
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'svg':
            case 'webp':
                return 'Image'
            case 'mp4':
            case 'avi':
            case 'mkv':
            case 'mov':
            case 'wmv':
            case 'flv':
            case 'webm':
                return 'Video'
            default:
                return 'Attachment'
        }
    }, [name, type])

    return (
        <div className="w-full p-2.5 bg-bg-secondary-10 rounded-sm hover:shadow-md gap-3">
            <GenericFilePreview
                name={name}
                size={size}
                preview={preview ?? ''}
                thumbnail={fileExtension === 'Image' ? preview : thumbnail}
                fileType={fileExtension}
                handlePreview={handlePreview}
                onDelete={onDelete}
            />
        </div>
    )
})

FileItem.displayName = 'FileItem'

export default FileItem
